import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Checkbox,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '@/services/api';
import { Subscriber } from '@/types';

export default function Subscribers() {
  const queryClient = useQueryClient();
  const { accounts } = useMsal();
  const user = accounts[0];
  const userName = user?.name || user?.username || 'User';

  const [selectedSubscribers, setSelectedSubscribers] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [emailsToAdd, setEmailsToAdd] = useState('');
  const [subscriberToDelete, setSubscriberToDelete] = useState<Subscriber | null>(null);

  const { data: subscribers, isLoading, refetch } = useQuery({
    queryKey: ['subscribers'],
    queryFn: () => api.getSubscribers(),
  });

  const addMutation = useMutation({
    mutationFn: (emails: string[]) => api.addSubscribers(emails, userName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscribers'] });
      setAddDialogOpen(false);
      setEmailsToAdd('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (email: string) => api.removeSubscriber(email, userName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscribers'] });
      setDeleteDialogOpen(false);
      setSubscriberToDelete(null);
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      const emails = Array.from(selectedSubscribers);
      for (const email of emails) {
        await api.removeSubscriber(email, userName);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscribers'] });
      setBulkDeleteDialogOpen(false);
      setSelectedSubscribers(new Set());
    },
  });

  const filteredSubscribers = (Array.isArray(subscribers) ? subscribers : []).filter(
    (s) => !searchTerm || s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSubscribers(new Set(filteredSubscribers.map((s) => s.email)));
    } else {
      setSelectedSubscribers(new Set());
    }
  };

  const handleSelectSubscriber = (email: string, checked: boolean) => {
    const newSelected = new Set(selectedSubscribers);
    if (checked) {
      newSelected.add(email);
    } else {
      newSelected.delete(email);
    }
    setSelectedSubscribers(newSelected);
  };

  const handleAddSubscribers = () => {
    const emails = emailsToAdd
      .split(/[\n,;]/)
      .map((e) => e.trim())
      .filter((e) => e);
    if (emails.length > 0) {
      addMutation.mutate(emails);
    }
  };

  const handleDeleteClick = (subscriber: Subscriber) => {
    setSubscriberToDelete(subscriber);
    setDeleteDialogOpen(true);
  };

  const formatDateTime = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Subscribers
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedSubscribers.size > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setBulkDeleteDialogOpen(true)}
            >
              Remove Selected ({selectedSubscribers.size})
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Subscribers
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          <TextField
            label="Search by email"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2, maxWidth: 400 }}
            fullWidth
          />

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={filteredSubscribers.length > 0 && selectedSubscribers.size === filteredSubscribers.length}
                      indeterminate={selectedSubscribers.size > 0 && selectedSubscribers.size < filteredSubscribers.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Added By</TableCell>
                  <TableCell>Added</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSubscribers.map((subscriber) => (
                  <TableRow key={subscriber.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedSubscribers.has(subscriber.email)}
                        onChange={(e) => handleSelectSubscriber(subscriber.email, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={subscriber.source}
                        size="small"
                        sx={{
                          backgroundColor: subscriber.source === 'API' ? '#E3F2FD' : '#E8F5E9',
                          color: subscriber.source === 'API' ? '#1565C0' : '#2E7D32',
                        }}
                      />
                    </TableCell>
                    <TableCell>{subscriber.email}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {subscriber.addedBy || '-'}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {formatDateTime(subscriber.createdAt)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(subscriber)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredSubscribers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No subscribers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
            Total: {Array.isArray(subscribers) ? subscribers.length : 0} subscribers
          </Typography>
        </>
      )}

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Subscribers</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter email addresses, one per line or separated by commas.
          </Typography>
          <TextField
            multiline
            rows={8}
            fullWidth
            value={emailsToAdd}
            onChange={(e) => setEmailsToAdd(e.target.value)}
            placeholder="user1@example.com&#10;user2@example.com"
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Duplicate emails will be skipped. Invalid emails will be ignored.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddSubscribers}
            disabled={addMutation.isPending}
          >
            {addMutation.isPending ? 'Adding...' : 'Add Subscribers'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Remove</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{subscriberToDelete?.email}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => subscriberToDelete && deleteMutation.mutate(subscriberToDelete.email)}
            disabled={deleteMutation.isPending}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Bulk Remove</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{selectedSubscribers.size}</strong> subscribers?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => bulkDeleteMutation.mutate()}
            disabled={bulkDeleteMutation.isPending}
          >
            Remove All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
